/*
 * Copyright (c) Funtip GbR 2018.
 * Simon Oldeboershuis, Perry Prust
 */

import { all, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { fetchWrapperFuncNoLoading } from '../../../utils';
import { handleFetchException } from '../../System/actions';
import { getPlaylistAdminApi } from '../../System/systemConfig';
import {
    ALBUM_LIST_BACKWARD,
    ALBUM_LIST_FAST_BACKWARD,
    ALBUM_LIST_FAST_FORWARD,
    ALBUM_LIST_FORWARD,
    ALBUM_LIST_SEARCH,
    searchAlbum,
    SEARCH_ALBUM,
    setAlbumList,
    setAlbumSearch,
    setAlbumSearchCount,
    setAlbumSearchCurrentPos
} from './actions';
import { albumListSearchString, albumSearchCount, albumSearchCurrentPos, albumSearchPageSize } from './selectors';

const playlistApi = getPlaylistAdminApi();

function* search(action: Action<string>) {
    yield put(setAlbumSearchCurrentPos(0));
    yield put(setAlbumSearch(action.payload));
    yield put(searchAlbum());
}

function* searchAlbumSearchGenerator(): {} {
    const searchString = yield select(albumListSearchString);
    const pageSize = yield select(albumSearchPageSize);
    const currentPos = yield select(albumSearchCurrentPos);

    try {
        yield* fetchWrapperFuncNoLoading(() => playlistApi.searchAlbums(currentPos, pageSize, searchString), [
            r => setAlbumList(r)
        ]);

        yield* fetchWrapperFuncNoLoading(() => playlistApi.countAlbums(searchString), [r => setAlbumSearchCount(r)]);
    } catch (err) {
        if (err instanceof Response) {
            yield put(handleFetchException(err));
        }
    }
}

function* albumListForwardGenerator() {
    const pageSize: number = yield select(albumSearchPageSize);
    const count: number = yield select(albumSearchCount);
    const currentPos: number = yield select(albumSearchCurrentPos);

    if (currentPos + pageSize <= count) {
        yield put(setAlbumSearchCurrentPos(currentPos + pageSize));
        yield put(searchAlbum());
    }
}

function* albumListBackwardGenerator() {
    const pageSize: number = yield select(albumSearchPageSize);
    const currentPos: number = yield select(albumSearchCurrentPos);

    if (currentPos > 0) {
        const nextPos = currentPos > pageSize ? currentPos - pageSize : 0;
        yield put(setAlbumSearchCurrentPos(nextPos));
        yield put(searchAlbum());
    }
}
function* albumListFastBackwardGenerator() {
    const currentPos: number = yield select(albumSearchCurrentPos);
    if (currentPos > 0) {
        yield put(setAlbumSearchCurrentPos(0));
        yield put(searchAlbum());
    }
}
function* albumListFastForwardGenerator() {
    const pageSize: number = yield select(albumSearchPageSize);
    const count: number = yield select(albumSearchCount);
    const currentPos: number = yield select(albumSearchCurrentPos);

    const nextPos = count - pageSize > 0 ? count - pageSize : 0;
    if (currentPos !== nextPos) {
        yield put(setAlbumSearchCurrentPos(nextPos));
        yield put(searchAlbum());
    }
}
function* watchForAlbumListPaging() {
    yield takeLatest(ALBUM_LIST_FORWARD, albumListForwardGenerator);
    yield takeLatest(ALBUM_LIST_BACKWARD, albumListBackwardGenerator);
    yield takeLatest(ALBUM_LIST_FAST_FORWARD, albumListFastForwardGenerator);
    yield takeLatest(ALBUM_LIST_FAST_BACKWARD, albumListFastBackwardGenerator);
    yield takeLatest(SEARCH_ALBUM, searchAlbumSearchGenerator);
    yield takeLatest(ALBUM_LIST_SEARCH, search);
}

export function* albumSagas() {
    yield all([watchForAlbumListPaging()]);
}
